/**
 * Knowledge-base citation parser for AI Assistant Core
 *
 * Parses [1] [2] citation markers in assistant text and
 * matches them against content chunks returned by the backend.
 */

// ---------------------------------------------------------------------------
// CitationParser
// ---------------------------------------------------------------------------

export interface Citation {
  /** Raw marker text, e.g. "[1]" */
  marker: string
  /** Numeric index, e.g. 1 */
  index: number
  /** Start position in the source text */
  start: number
  /** End position in the source text */
  end: number
}

export class CitationParser {
  /** Regex for citation markers: [1], [12], etc. */
  private static readonly MARKER_RE = /\[(\d+)\]/g

  /**
   * Extract all citation markers from a text block.
   *
   * @param text — source text containing [1], [2], … markers
   * @returns ordered list of citations with positions
   */
  parse(text: string): Citation[] {
    const citations: Citation[] = []
    let match: RegExpExecArray | null
    CitationParser.MARKER_RE.lastIndex = 0
    while ((match = CitationParser.MARKER_RE.exec(text)) !== null) {
      citations.push({
        marker: match[0],
        index: parseInt(match[1], 10),
        start: match.index,
        end: match.index + match[0].length,
      })
    }
    return citations
  }

  /**
   * Strip citation markers from text, returning clean text and a mapping.
   *
   * @returns `{ text: string, citations: Citation[] }`
   */
  strip(text: string): { text: string; citations: Citation[] } {
    const citations = this.parse(text)
    if (citations.length === 0) {
      return { text, citations: [] }
    }

    let cleaned = ''
    let lastEnd = 0
    for (const c of citations) {
      cleaned += text.slice(lastEnd, c.start)
      lastEnd = c.end
    }
    cleaned += text.slice(lastEnd)

    return { text: cleaned.trim(), citations }
  }

  /**
   * Replace citation markers with custom formatter output.
   *
   * @param text    — source text
   * @param format  — callback receiving citation index, returns replacement string
   */
  replace(text: string, format: (index: number) => string): string {
    return text.replace(CitationParser.MARKER_RE, (_match, num) => format(parseInt(num, 10)))
  }
}

// ---------------------------------------------------------------------------
// ChunkMatcher
// ---------------------------------------------------------------------------

export interface KnowledgeChunk {
  /** Chunk index (1-based, matching citation numbers) */
  index: number
  /** Source title or document name */
  title: string
  /** Chunk text content */
  content: string
  /** Optional source URL or identifier */
  source?: string
  /** Relevance score (0–1) */
  score?: number
}

export interface MatchedChunk extends KnowledgeChunk {
  /** Citation markers that reference this chunk */
  citedBy: number[]
}

export class ChunkMatcher {
  /**
   * Match parsed citations to a list of knowledge chunks.
   *
   * @param citations — output from `CitationParser.parse()`
   * @param chunks    — chunks returned by backend (should be 1-based indexed)
   * @returns chunks that were cited, with `citedBy` populated
   */
  match(citations: Citation[], chunks: KnowledgeChunk[]): MatchedChunk[] {
    const citedIndices = new Set(citations.map(c => c.index))
    const indexToCitations = new Map<number, number[]>()

    for (const c of citations) {
      const arr = indexToCitations.get(c.index) ?? []
      arr.push(c.index)
      indexToCitations.set(c.index, arr)
    }

    const matched: MatchedChunk[] = []
    for (const chunk of chunks) {
      if (citedIndices.has(chunk.index)) {
        matched.push({
          ...chunk,
          citedBy: indexToCitations.get(chunk.index) ?? [chunk.index],
        })
      }
    }

    // Preserve chunk order
    matched.sort((a, b) => a.index - b.index)
    return matched
  }

  /**
   * Build a flat map: citation index → chunk.
   *
   * @returns Map where key is citation number and value is the matching chunk
   */
  buildIndexMap(chunks: KnowledgeChunk[]): Map<number, KnowledgeChunk> {
    const map = new Map<number, KnowledgeChunk>()
    for (const chunk of chunks) {
      map.set(chunk.index, chunk)
    }
    return map
  }

  /**
   * Find chunks that are NOT cited in the text (orphans).
   */
  findOrphans(citations: Citation[], chunks: KnowledgeChunk[]): KnowledgeChunk[] {
    const cited = new Set(citations.map(c => c.index))
    return chunks.filter(c => !cited.has(c.index))
  }
}
